import { appConfig } from '../../constants/app-config';

export const ablyClientServiceModule = angular
  .module('Q.services.ably-client-service', [])

  .factory('ablyClientService', function ($log, $window, $rootScope, $http) {
    const clients = {};

    function destroy(svc) {
      try {
        if (svc.client) {
          svc.client.connection.close();
        }
        delete clients[svc.$clientKey];
        delete svc.channel;
        delete svc.client;
        delete svc.$clientKey;
        delete svc.$channelKey;
        delete svc.channelError;
      } catch (ex) {}
    }

    return {
      /**
       * Dresses an existing service by creating or re-using appropriate Ably client and channel
       * @param {*} svc - Service object
       * @param {String} ablyKey? -
       * @param {String} clientKey? -
       * @param {String} channelId -
       * @param {String} clientId -
       * @param {String} tokenUrl - optional URL, will default to main ably endpoint if not provided
       * @param {String} channelEventPrefix -
       * @returns {*} Socket
       */
      connect({
        svc,
        clientKey,
        channelId,
        clientId,
        tokenUrl,
        channelEventPrefix = 'socket',
      }) {
        const channelKey = `${clientKey}\n${channelId}`;
        if (
          svc.channel &&
          svc.$channelKey === channelKey &&
          !svc.channelError
        ) {
          return svc;
        } else if (
          svc.channelError ||
          (svc.client && svc.$channelKey !== channelKey)
        ) {
          // only allow one channel per service (this could be a bad idea)
          destroy(svc);
        }

        // delete in case there was a previous channel error
        delete svc.channelError;

        try {
          if (!$window.Ably) {
            throw new Error('Ably client code not loaded');
          }
          if (!clientKey && !svc.client) {
            throw new Error('No key found for Ably');
          }
          if (!channelId) {
            throw new Error('Unable to find channel ID');
          }

          svc.$channelKey = channelKey;
          svc.$clientKey = clientKey;

          const refreshToken = () =>
            $http
              .post(tokenUrl || `${appConfig.baseApiRoot}ably/token`)
              .then((resp) => (resp.data || {}).token);

          if (!clients[clientKey] && !svc.client) {
            clients[clientKey] = new $window.Ably.Realtime({
              clientId: clientId || null,
              recover: (lastConnectionDetails, cb) => {
                // Always attempt recovery in Ably 2.0
                cb(true);
              },
              authCallback: () => {
                return refreshToken().catch((e) => {
                  // status <= 0 is a client-side network failure or timeout;
                  // Ably retries authCallback itself, so don't alert on it
                  const isNetworkError = e && e.status <= 0;
                  $log[isNetworkError ? 'warn' : 'error'](
                    '[Ably] Token refresh failed',
                    e,
                  );
                  throw e;
                });
              },
            });

            // Monitor connection failures
            clients[clientKey].connection.on('failed', () => {
              $log.error('[Ably] Connection failed', { clientKey });
            });
          }
          svc.client = svc.client || clients[clientKey];

          // provide the ability to listen to other channels and broadcast their connection status
          svc.listen = (channelId, eventPrefix) => {
            const channel = svc.client.channels.get(channelId);

            svc.client.connection.on('connected', function () {
              $rootScope.$broadcast(`${eventPrefix}:connected`);
              // ensure the channel is attached, used for when a connection has to reconnect itself
              if (channel) {
                channel.attach();
              }
              $log.debug(
                `${clientKey} - ${channelId} socket channel is ready.`,
              );
            });

            channel.on('detached', function () {
              $log.debug(
                `${clientKey} - ${channelId} socket channel is detached`,
              );
              $rootScope.$broadcast(`${eventPrefix}:disconnected`);
              // ensure that if the channel gets disconnected that it connects again
              if (svc.client.connection.state === 'connected') {
                channel.attach();
              }
            });

            channel.on('failed', function (err) {
              $log.error(`[Ably] Channel failed`, {
                clientKey,
                channelId,
                error: err,
              });
            });

            $log.debug(`Channel subscribed: ${clientKey} - ${channelId}`);
            return channel;
          };

          svc.channel = svc.listen(channelId, channelEventPrefix);
        } catch (ex) {
          destroy(svc);
          svc.channelError = ex || new Error('Error creating Ably connection');
          $log.error(
            `Error creating Ably connection (${channelEventPrefix}:${clientKey})`,
            ex,
            {
              channelId,
              clientId,
              channelEventPrefix,
            },
          );
        }

        return svc;
      },
      destroy,
    };
  });
